// SegmentedControl — compact inline SINGLE-SELECT among 2–5 ordered options
// (access level None/View/Manage, view-mode, density). Native-owned ([ADR-028]):
// real <input type="radio"> visually hidden (`peer sr-only`) behind token-styled
// connected segments, each in its own <label> — so the WAI-ARIA radiogroup
// keyboard model (Arrow keys move + select, roving focus) comes FREE, the same
// technique as RadioGroup, a distinct compact presentation (no behavior fork).
//
// NOT for multi-select, long lists, or free-form choices — use a Checkbox group /
// the picker. Controlled: `value` + `onChange(value)`; `value` is always
// one of `options`. Validation, if any, is form-state-level (RHF, [ADR-008]).

import { useId, type ReactNode } from 'react';

export interface SegmentedOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  /** Controlled single-select value — always one of `options`. */
  value: string;
  onChange: (value: string) => void;
  /** Shared radio-input name; defaults to a generated id. */
  name?: string;
  /** Accessible name for the group (WCAG 4.1.2) — required unless `labelledBy`. */
  ariaLabel?: string;
  /** Element id of a visible label, for aria-labelledby. */
  labelledBy?: string;
  disabled?: boolean;
  /** Compact (default) for inline use; `md` for a standalone field. */
  size?: 'sm' | 'md';
  className?: string;
}

const sizeClass: Record<'sm' | 'md', string> = {
  sm: 'text-xs px-2.5 py-1',
  // `md` is the standalone-FIELD size — it must sit at the field-frame height
  // (2.5rem incl. border) so a segmented beside a PickerField/Input reads as one
  // row, not a shorter cousin (the asset Location step caught the mismatch).
  md: 'text-sm px-3.5 min-h-[calc(2.5rem-2px)] inline-flex items-center',
};

export function SegmentedControl({
  options,
  value,
  onChange,
  name: nameProp,
  ariaLabel,
  labelledBy,
  disabled = false,
  size = 'sm',
  className = '',
}: SegmentedControlProps) {
  const reactId = useId();
  const name = nameProp ?? reactId;

  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : ariaLabel}
      aria-labelledby={labelledBy}
      className={`inline-flex rounded-md border border-[var(--color-border)] ${disabled ? 'opacity-60' : ''} ${className}`}
    >
      {options.map((opt) => {
        const optDisabled = disabled || opt.disabled;
        const selected = value === opt.value;
        return (
          <label
            key={opt.value}
            className={
              'relative inline-flex items-center justify-center border-s border-[var(--color-border)] first:border-s-0 ' +
              'first:rounded-s-md last:rounded-e-md transition-colors ' +
              sizeClass[size] +
              (optDisabled ? ' cursor-not-allowed opacity-50' : ' cursor-pointer') +
              (selected
                ? ' bg-[var(--color-primary-display)]/12 font-medium text-[var(--color-primary-display)]'
                : ' text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]')
            }
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={selected}
              disabled={optDisabled}
              onChange={() => onChange(opt.value)}
              className="peer sr-only"
            />
            {/* Focus ring rides the segment (the input is sr-only). Inset ring so it
                follows the rounded first/last corners and never clips on neighbors. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[inherit] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-[var(--color-primary-ring)]"
            />
            <span className="relative whitespace-nowrap">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
